import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { useAuth, getDefaultRoute } from "../context/AuthContext";
import api from "../lib/api";
import { getErrorMessage } from "../lib/formatters";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [resetForm, setResetForm] = useState({ password: "", confirmPassword: "" });
  const [pendingReset, setPendingReset] = useState(null);
  const [loading, setLoading] = useState(false);

  const redirectTarget = useMemo(() => new URLSearchParams(location.search).get("redirect"), [location.search]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const profile = await login(form.email, form.password);
      if (profile?.requires_password_reset) {
        setPendingReset({
          email: profile.user.email,
          name: profile.user.name,
          role: profile.user.role,
          token: profile.reset_token,
        });
        setResetForm({ password: "", confirmPassword: "" });
        toast.info("Create a new password to activate this account.");
        return;
      }
      toast.success(`Welcome back, ${profile.user.name}`);
      navigate(redirectTarget || getDefaultRoute(profile.user), { replace: true });
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    if (!pendingReset) return;
    if (resetForm.password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (resetForm.password !== resetForm.confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token: pendingReset.token, new_password: resetForm.password });
      const profile = await login(pendingReset.email, resetForm.password);
      toast.success(`Password updated. Welcome, ${profile.user.name}`);
      setForm({ email: pendingReset.email, password: resetForm.password });
      setPendingReset(null);
      setResetForm({ password: "", confirmPassword: "" });
      navigate(redirectTarget || getDefaultRoute(profile.user), { replace: true });
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen overflow-hidden bg-[linear-gradient(180deg,_#faf8f3_0%,_#fffdf8_100%)] px-4 py-4 sm:px-5 sm:py-5 lg:px-6 lg:py-5">
      <div className="mx-auto grid min-h-[calc(100vh-2.5rem)] max-w-[1460px] gap-4 lg:h-[calc(100vh-2.5rem)] lg:min-h-0 lg:grid-cols-[1.22fr_0.78fr] lg:gap-4">
        <section className="flex items-center justify-center lg:h-full lg:justify-end" data-testid="login-hero-section">
          <div className="flex h-full min-h-[320px] w-full items-center justify-center sm:min-h-[430px] lg:min-h-0" data-testid="login-hero-image-frame">
            <img
              src="https://customer-assets.emergentagent.com/job_smartclock-in/artifacts/jdg97zub_Animated_character_scanning_202604201353.jpeg"
              alt="Tarka HR attendance illustration"
              className="block h-auto max-h-[72vh] w-full max-w-[920px] object-contain lg:max-h-[calc(100vh-5rem)]"
              data-testid="login-hero-image"
            />
          </div>
        </section>

        <section className="flex items-center justify-center lg:h-full" data-testid="login-form-section">
          <div className="w-full max-w-[430px] lg:-translate-x-2" data-testid="login-form-wrapper">
            <Card className="border-[#0D1B2A]/10 bg-white shadow-[0_24px_70px_rgba(13,27,42,0.10)]" data-testid="login-card">
              <CardHeader className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#C27D00]" data-testid="login-card-caption">{pendingReset ? "First login setup" : "Secure sign in"}</p>
                <CardTitle className="text-3xl font-extrabold" data-testid="login-card-title">{pendingReset ? "Set your new password" : "Enter your workspace"}</CardTitle>
                {pendingReset ? <CardDescription data-testid="login-card-description">Your temporary password worked. Create a fresh password to continue.</CardDescription> : <CardDescription data-testid="login-card-subcopy">Use your company credentials to continue securely.</CardDescription>}
              </CardHeader>
              <CardContent>
                {pendingReset ? (
                  <form className="space-y-4" onSubmit={handleResetPassword} data-testid="login-reset-form">
                    <div className="rounded-3xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="login-reset-banner">
                      <p className="font-semibold" data-testid="login-reset-banner-name">{pendingReset.name}</p>
                      <p className="mt-1" data-testid="login-reset-banner-email">{pendingReset.email}</p>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700" htmlFor="reset-password" data-testid="reset-password-label">New password</label>
                      <Input
                        id="reset-password"
                        type="password"
                        value={resetForm.password}
                        onChange={(event) => setResetForm((current) => ({ ...current, password: event.target.value }))}
                        className="h-12 rounded-2xl border-slate-200"
                        data-testid="reset-password-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700" htmlFor="reset-confirm-password" data-testid="reset-confirm-password-label">Confirm password</label>
                      <Input
                        id="reset-confirm-password"
                        type="password"
                        value={resetForm.confirmPassword}
                        onChange={(event) => setResetForm((current) => ({ ...current, confirmPassword: event.target.value }))}
                        className="h-12 rounded-2xl border-slate-200"
                        data-testid="reset-confirm-password-input"
                      />
                    </div>
                    <div className="flex flex-wrap gap-3" data-testid="login-reset-actions">
                      <Button type="submit" className="h-12 flex-1 rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" disabled={loading} data-testid="reset-submit-button">
                        {loading ? "Updating password..." : "Save and continue"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="h-12 rounded-2xl"
                        onClick={() => {
                          setPendingReset(null);
                          setResetForm({ password: "", confirmPassword: "" });
                        }}
                        data-testid="reset-cancel-button"
                      >
                        Back to login
                      </Button>
                    </div>
                  </form>
                ) : (
                  <form className="space-y-4" onSubmit={handleSubmit} data-testid="login-form">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700" htmlFor="login-email" data-testid="login-email-label">Email</label>
                      <Input
                        id="login-email"
                        type="email"
                        value={form.email}
                        onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
                        className="h-12 rounded-2xl border-slate-200"
                        data-testid="login-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700" htmlFor="login-password" data-testid="login-password-label">Password</label>
                      <Input
                        id="login-password"
                        type="password"
                        value={form.password}
                        onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
                        className="h-12 rounded-2xl border-slate-200"
                        data-testid="login-password-input"
                      />
                    </div>
                    <Button type="submit" className="h-12 w-full rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" disabled={loading} data-testid="login-submit-button">
                      {loading ? "Signing in..." : "Sign in to Tarka HR"}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </div>
  );
}